import React from "react";

// iter_21: image moved from a left column to directly beneath the "You've
// been carrying this for a long time." heading. Image source swapped to a
// new editorial portrait while the "From Austine" caption below the image
// remains byte-identical in text, font, size, color, placement, and styling.
const NARRATIVE_IMG = "https://customer-assets-eiarnc6j.emergentagent.net/job_practice-portal-15/artifacts/0005a5b0af5cee9f_pexels-liza-summer-6382721.webp";

const bullets = [
    "Anxiety, overthinking, or self-doubt",
    "Feeling emotionally overwhelmed or shut down",
    "Worry about being rejected or not good enough",
    "Trouble fully trusting other people",
    "Painful relationship patterns that repeat",
    "Shame that never quite goes away",
];

export default function Narrative() {
    return (
        <section id="approach" data-testid="narrative-section" className="py-28 lg:py-40" style={{ background: "#FAFAF8" }}>
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="max-w-3xl" data-reveal>
                    <span className="editorial-eyebrow">You’re not alone in this</span>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                        You've been carrying this <em className="italic text-gold">for a long time.</em>
                    </h2>

                    {/* Image + caption — moved here in iter_21 from the former
                        left column so it reads directly beneath the heading.
                        Image frame, star ornament, and caption copy/styling
                        are preserved from the original left-column block. */}
                    <div className="mt-12">
                        <div className="relative">
                            <img
                                src={NARRATIVE_IMG}
                                alt="Quiet moment of reflection"
                                className="w-full h-[520px] lg:h-[640px] object-cover object-center"
                            />
                            <div className="absolute -bottom-6 -right-6 hidden lg:block">
                                <span className="font-serif text-gold text-7xl leading-none">✦</span>
                            </div>
                        </div>
                        <div className="mt-8 max-w-md">
                            <p className="editorial-eyebrow" data-testid="narrative-caption">From Austine</p>
                            <p className="mt-4 font-serif italic text-lg text-charcoal-soft leading-relaxed">
                                If any of what follows feels familiar — even a small piece of it —
                                you are not alone, and you are not too late.
                            </p>
                        </div>
                    </div>

                    <div className="mt-12 hairline" />

                    <p className="mt-10 text-lg leading-[1.85] text-charcoal-soft drop-cap">
                        You've spent years trying to move forward, telling yourself you should be over it by now. Maybe you've become good at staying busy, pushing your feelings aside, or pretending everything is fine. From the outside, it may even look like you have it together. But underneath, you still carry the weight of what happened.
                    </p>

                    <p className="mt-6 text-lg leading-[1.85] text-charcoal-soft">
                        The emotional wounds from your past — the rejection, the criticism, the quiet rooms where you felt unseen — still affect you in ways that are painful and exhausting. You second-guess yourself. You worry people will judge you, leave you, or discover that you are somehow not enough.
                    </p>

                    <p className="mt-6 text-lg leading-[1.85] text-charcoal-soft">
                        Sometimes you can keep those feelings buried. Then something small happens — a certain tone of voice, a difficult conversation, a memory in a quiet moment — and suddenly you are overwhelmed.
                    </p>

                    <blockquote
                        className="mt-10 pull-quote"
                        style={{
                            borderLeft: "3px solid #997777",
                            paddingLeft: "20px",
                            color: "#997777",
                        }}
                    >
                        If this sounds familiar, you are not broken. You are carrying what you had to in order to survive.
                    </blockquote>

                    <div className="mt-12">
                        <p className="editorial-eyebrow text-charcoal/70">You might notice that you…</p>
                        <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-4" data-testid="narrative-bullets">
                            {bullets.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-charcoal-soft">
                                    <span className="mt-2 h-px w-4 bg-gold flex-shrink-0" />
                                    <span className="text-[15px] leading-relaxed">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
